import React, { ReactNode, useCallback, useMemo } from 'react';
import { FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';
import ActionsUpload from '@components/Actions/ActionsUpload';
import ActionTypes, { LorawanActionTypes } from '@utils/deviceActions';
import { DeviceActionStepsProps } from '../../../ActionsModal/types';

interface UseUploadProps {
  actionKey: number | undefined;
  form: FormInstance<any> | undefined;
  customInfo?: ReactNode;
  disabled?: boolean;
  onRemove?: () => void;
  isLoraWan?: boolean;
}

const useUpload = ({
  actionKey,
  form,
  customInfo,
  disabled,
  onRemove,
  isLoraWan,
}: UseUploadProps): DeviceActionStepsProps => {
  const { t } = useTranslation(['device-actions-modal', 'attributes']);

  const selectedAction = useMemo(() => actionKey ?? -1, [actionKey]);

  const handleUpload = useCallback(
    (devices: { [key: string]: string }[] | undefined) => {
      if (devices && devices?.length === 0) {
        const emptyCSVText = `* ${t('empty-csv')}`;
        form?.setFields([
          {
            name: 'uploadedItems',
            errors: [emptyCSVText],
          },
        ]);
      }
      form?.setFieldValue('uploadedItems', devices);
    },
    [form, t],
  );

  const csvFilename = useMemo(() => {
    const actionName = (
      isLoraWan
        ? LorawanActionTypes[selectedAction]
        : ActionTypes[selectedAction]
    )
      ?.toLowerCase()
      .replaceAll('_', '-');
    const defaultName = 'import-model';
    return actionName ? `${actionName}-${defaultName}` : defaultName;
  }, [selectedAction, isLoraWan]);

  return {
    title: t('upload.title'),
    name: 'uploadedItems',
    disabledValueFromEvent: true,
    content: (
      <ActionsUpload
        actionKey={actionKey}
        onUpload={handleUpload}
        onRemove={onRemove}
        customInfo={customInfo}
        csvFilename={csvFilename}
        disabled={disabled}
        isLoraWan={isLoraWan}
      />
    ),
  };
};

export default useUpload;
